import React, { ReactNode } from 'react';
import { experimentalStyled, Container, Box, Theme } from '@mui/material';

import { PageContainer } from '@europrocurement/flexy-components/pages';
import {
    ActionsProvider,
    ContextActionType,
    useModal,
} from '@europrocurement/flexy-components/providers';
import { TopbarHeight } from '../../../assets/global/Theme-variable';
import {
    Sidebar,
    Header,
    type MenuEntry,
    HeaderProps,
    FooterProps,
    SidebarProps,
    UserPreferencesProps,
    UserPreferences,
} from '../../organisms';
import { Modalizer } from '../../molecules';
import { ContextActionsMenu } from '../../organisms/ContextActionsMenu/ContextActionsMenu';
import { FullLayoutOutlet } from './FullLayoutOutlet';

export type FullLayoutProps = {
    menuEntries: MenuEntry[];
    footerProps?: FooterProps;
    headerProps?: HeaderProps;
    userPreferencesProps?: UserPreferencesProps;
    contextActionMainIcon?: ReactNode;
    defaultContextActions?: ContextActionType[];
    logo: SidebarProps['logo'];
    logoFolded: SidebarProps['logoFolded'];
    sidebarActions?: SidebarProps['actions'];
    pageTitle?: string;
    source?: 'BACK' | 'FRONT';
};

const MainWrapper = experimentalStyled('div')(() => ({
    display: 'flex',
    overflow: 'hidden',
    width: '100%',
    height: '100vh',
    paddingTop: TopbarHeight,
}));

const PageWrapper = experimentalStyled('div')(({ theme }: { theme: Theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
}));

export const FullLayout: React.FunctionComponent<FullLayoutProps> = function (
    props: FullLayoutProps,
) {
    const {
        menuEntries,
        footerProps,
        headerProps,
        userPreferencesProps,
        contextActionMainIcon,
        defaultContextActions = [],
        logo,
        logoFolded,
        sidebarActions = [],
        pageTitle = 'Back2Digital',
        source = 'FRONT',
    } = props;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
    );

    const { modalStatus, modalActions } = useModal();

    return (
        <PageContainer
            title={pageTitle}
            description="Backoffice Legal2digital"
        >
            <ActionsProvider defaultActions={defaultContextActions}>
                <MainWrapper
                    style={{
                        height: isMobile ? 'auto' : '100vh',
                    }}
                    data-testid="test-id-FullLayout"
                >
                    <Header {...headerProps} />

                    <Sidebar
                        menuEntries={menuEntries}
                        logo={logo}
                        logoFolded={logoFolded}
                        actions={sidebarActions}
                    />

                    <PageWrapper>
                        <Container
                            maxWidth={false}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            <FullLayoutOutlet
                                footerProps={footerProps}
                                source={source}
                            />
                        </Container>
                    </PageWrapper>
                    <ContextActionsMenu mainIcon={contextActionMainIcon} />
                    {userPreferencesProps ? <UserPreferences {...userPreferencesProps} /> : null}
                    {/* <Customizer showButton={false} /> */}
                </MainWrapper>
            </ActionsProvider>
            <Modalizer
                open={modalStatus.isOpen}
                fullWidth={modalStatus.isFullWidth}
                isDraggable={modalStatus.isDraggable}
                onClose={() => modalActions.close()}
                maxWidth="xl"
            >
                <Box>{modalStatus.content}</Box>
            </Modalizer>
        </PageContainer>
    );
};
